import { useState, type ReactNode } from 'react';
import { Video } from 'lucide-react';
import { Button } from '@/components/ui';
import { cn } from '@/lib/cn';
import { useHabitLog } from '@/features/habits/useHabitLog';
import { useTaskAction } from '@/features/lifecycle/useTaskAction';
import { hapticTap } from '@/features/lifecycle/haptics';
import { habitColor } from '@/lib/habitColor';
import { meetingUrlFor, openMeetingUrl } from '@/lib/meetingLink';
import { formatTimeRange } from '@/lib/time';
import { NowCard } from './NowCard';
import { NowHabitCard } from './NowHabitCard';
import { NowMeetingCard } from './NowMeetingCard';
import { nowItemKey, priorityAccentClass, type NowItem } from './todaySelectors';

export interface NowStackProps {
  /** Every item concurrent with now, in stable display order (useTodayAgenda's nowItems). */
  items: NowItem[];
  /** Expansion fallback when the user hasn't chosen a row (useTodayAgenda's defaultNowKey). */
  defaultKey: string | undefined;
  now: Date;
}

/**
 * The Now area, shared by mobile Today and the desktop Narrator rail: every
 * item concurrent with now in one vertical stack, ordered by start time.
 * Exactly one item is expanded as the full hero card; the rest render as
 * compact rows with their key action inline. Tapping a compact row expands it
 * in place (the previous hero collapses) — the order NEVER changes on
 * interaction. When the expanded item leaves the now set (completed, ended,
 * skipped), expansion falls back to the default heuristic. No expand/collapse
 * animation: this is a tens-per-day interaction (motion budget: none).
 */
export function NowStack({ items, defaultKey, now }: NowStackProps) {
  // The user's chosen row, if any and still concurrent; else the default.
  const [chosenKey, setChosenKey] = useState<string | null>(null);
  if (items.length === 0) return null;
  const expandedKey =
    chosenKey !== null && items.some((i) => nowItemKey(i) === chosenKey) ? chosenKey : defaultKey;

  return (
    <ul className="flex flex-col gap-1.5">
      {items.map((item) => {
        const key = nowItemKey(item);
        const expanded = key === expandedKey;
        return (
          // aria-expanded on the wrapper marks which stack entry is the hero;
          // the compact rows carry aria-expanded="false" on their expand control.
          <li key={key} {...(expanded ? { 'aria-expanded': true } : {})}>
            {expanded ? (
              <ExpandedNowCard item={item} now={now} />
            ) : (
              <CompactNowRow item={item} onExpand={() => setChosenKey(key)} />
            )}
          </li>
        );
      })}
    </ul>
  );
}

// The full hero for the expanded item — the same kind → card mapping the old
// single-item cascade used, so the expanded state never regresses.
function ExpandedNowCard({ item, now }: { item: NowItem; now: Date }) {
  if (item.kind === 'task') return <NowCard task={item.task} now={now} />;
  if (item.kind === 'habit') {
    return (
      <NowHabitCard
        habit={item.habit}
        start={item.start}
        end={item.end}
        isDone={item.isDone}
        now={now}
      />
    );
  }
  return <NowMeetingCard event={item.event} now={now} />;
}

const titleButtonClass =
  'block w-full truncate rounded-sm text-left text-body font-medium leading-tight text-t1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-inset';

/**
 * A collapsed stack entry: accent bar, title + time range, and the item's key
 * action inline. Tapping the body (or the title button, for keyboard users)
 * expands it; the closest('button') guard keeps the inline action from
 * expanding.
 */
function CompactNowRow({ item, onExpand }: { item: NowItem; onExpand: () => void }) {
  const { toggleToday } = useHabitLog();
  const { start: startTask, complete } = useTaskAction();

  const expand = () => {
    void hapticTap();
    onExpand();
  };

  let title: string;
  let accent: ReactNode;
  let action: ReactNode = null;
  // Meetings keep Up next's external treatment: dashed hairline on bg-canvas.
  const external = item.kind === 'calendar-event';

  if (item.kind === 'task') {
    const t = item.task;
    const isLive = t.status === 'in_progress';
    title = t.title;
    accent = (
      <span
        className={cn('h-7 w-[3px] shrink-0 rounded-full', priorityAccentClass(t.priority))}
        aria-hidden
      />
    );
    action = (
      <Button
        size="sm"
        variant="secondary"
        className="shrink-0"
        aria-label={`${isLive ? 'Complete' : 'Start'} ${t.title}`}
        onClick={() => (isLive ? complete(t.id) : startTask(t.id))}
      >
        {isLive ? 'Done' : 'Start'}
      </Button>
    );
  } else if (item.kind === 'habit') {
    const h = item.habit;
    title = h.name;
    accent = (
      <span
        className="h-7 w-[3px] shrink-0 rounded-full"
        style={{ backgroundColor: habitColor(h.color) }}
        aria-hidden
      />
    );
    action = (
      <Button
        size="sm"
        variant="secondary"
        className="shrink-0"
        aria-label={`${h.name} — ${item.isDone ? 'done today, tap to undo' : 'not yet done, tap to log'}`}
        onClick={() => toggleToday(h)}
      >
        {item.isDone ? 'Undo' : 'Done'}
      </Button>
    );
  } else {
    const e = item.event;
    const joinUrl = meetingUrlFor(e);
    title = e.title;
    accent = <span className="h-7 w-[3px] shrink-0 rounded-full bg-info" aria-hidden />;
    if (joinUrl) {
      action = (
        <Button
          size="sm"
          variant="secondary"
          className="shrink-0"
          aria-label={`Join ${e.title}`}
          leadingIcon={<Video className="h-3.5 w-3.5 text-info" aria-hidden />}
          onClick={() => {
            void hapticTap();
            void openMeetingUrl(joinUrl);
          }}
        >
          Join
        </Button>
      );
    }
  }

  return (
    <div
      className={cn(
        'pressable flex w-full cursor-pointer items-center gap-3 rounded-xl border px-3 py-2 transition-colors pointer-coarse:min-h-[44px] can-hover:hover:border-line-3',
        external ? 'border-dashed border-line-2 bg-canvas' : 'border-line-2 bg-card',
      )}
      onClick={(e) => {
        if ((e.target as HTMLElement).closest('button')) return;
        expand();
      }}
    >
      {accent}
      <span className="min-w-0 flex-1">
        {/* Real button so keyboard/screen-reader users can expand — the
            row-level onClick is pointer convenience only. */}
        <button type="button" aria-expanded={false} className={titleButtonClass} onClick={expand}>
          {title}
        </button>
        {/* Sans time range per the mono ceiling — inline meta, not a live/columnar number. */}
        <span className="mt-0.5 block text-caption text-t2">
          {formatTimeRange(item.start, item.end)}
        </span>
      </span>
      {action}
    </div>
  );
}
